import axios from 'axios';
import {getCurrentUser, logout} from './authService';

const API_URL = process.env.REACT_APP_API_URL;

const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use(config => {
    const user = getCurrentUser();
    if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    return config;
}, error => {
    return Promise.reject(error);
});

apiClient.interceptors.request.use(config => {
    const user = getCurrentUser();
    if (user && user.token) {
        config.headers['Authorization'] = `Bearer ${user.token}`;
    }
    return config;
}, error => {
    return Promise.reject(error);
});

apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 401) {
            logout();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const inviteUser = async (email, role) => {
    try {
        const response = await apiClient.post('/users/invite', null, {
            params: {
                email,
                role
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Invitation failed');
    }
};

export const reInviteUser = async (email, role) => {
    try {
        const response = await apiClient.post('/users/re-invite', null, {
            params: {
                email,
                role
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Invitation failed');
    }
};

export const updateUserRole = async (email, role) => {
    try {
        const response = await apiClient.post('/users/change-role', null, {
            params: {
                email,
                role
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed');
    }
};

export const updateUserPublishMode = async (email, isPublishModeActive) => {
    try {
        const response = await apiClient.post('/users/change-publish-mode', null, {
            params: {
                email,
                isPublishModeActive
            }
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed');
    }
};

export const updateUserPassword = async (username, newPassword) => {
    const response = await apiClient.put(`/users/change/${username}/password`, newPassword, {
        headers: {
            'Content-Type': 'text/plain',
        },
    });
    return response.data;
};

export const fetchUsers = async () => {
    const response = await apiClient.get(`${API_URL}/users`);
    return response.data;
};

export const deleteUser = async (id) => {
    const response = await apiClient.delete(`${API_URL}/users/${id}`);
    return response;
};

export const fetchProjects = async () => {
    const response = await apiClient.get(`${API_URL}/projects`);
    return response.data;
};

export const addProject = async (name, locales) => {
    const response = await apiClient.post(
        `${API_URL}/projects`,
        {locales},
        {
            params: {
                projectName: name,
            }
        }
    );
    return response.data;
};

export const deleteProject = async (id) => {
    const response = await apiClient.delete(`${API_URL}/projects/${id}`);
    return response.data;
};

export const assignUserToProject = async (projectName, username) => {
    const response = await apiClient.post(`${API_URL}/projects/pin/${projectName}/${username}`);
    return response.data;
};

export const deleteUserFromProject = async (projectName, username) => {
    const response = await apiClient.post(`${API_URL}/projects/delete/${projectName}/${username}`);
    return response.data;
}

export const getMyProjects = async (username) => {
    const response = await apiClient.get(`${API_URL}/my-projects/${username}`);
    return response.data;
};

export const getResources = async (projectId) => {
    const response = await apiClient.get(`${API_URL}/resource/${projectId}`);
    return response.data;
};

export const getResourcesNoValue = async (projectId) => {
    const response = await apiClient.get(`${API_URL}/resource/no-values/${projectId}`);
    return response.data;
};

export const getProjectLocales = async (projectName) => {
    const response = await apiClient.get(`${API_URL}/locales/get-by-project/${projectName}`);
    return response.data;
};

export const addResource = async (projectId, keyName, description, data, isHtml) => {
    const response = await apiClient.post(`${API_URL}/resource/${projectId}/${keyName}`, data, {
        params: {
            isHtml,
            description
        }
    });
    return response.data;
};

export const deleteResource = async (projectId, keyName) => {
    const response = await apiClient.delete(`${API_URL}/resource/${projectId}/${keyName}`, {});
    return response.data;
};

export const publishResources = async (projectId) => {
    const response = await apiClient.post(`${API_URL}/resource/publish/${projectId}`, {});
    return response.data;
};

export const getTempResources = async (projectId) => {
    const response = await apiClient.get(`${API_URL}/resource/temps/${projectId}`, {});
    return response.data;
};

export const deleteTempResources = async (projectId) => {
    const response = await apiClient.delete(`${API_URL}/resource/temps/${projectId}`, {});
    return response.data;
};

export const deleteAllResources = async (projectId) => {
    const response = await apiClient.delete(`${API_URL}/resource/delete-all/${projectId}`, {});
    return response.data;
};

export const editResource = async (projectId, keyName, description, editedTranslations, isHtml, newKeyName) => {
    const response = await apiClient.put(`${API_URL}/resource/${projectId}/${keyName}`, {data: editedTranslations}, {
        params: {
            isHtml,
            newKeyName,
            description
        }
    });
    return response.data;
};

export const importResource = async (projectId, data) => {
    const response = await apiClient.post(`${API_URL}/resource/${projectId}/import`, data);
    return response.data;
};

export const changePassword = async (username, newPassword, oldPassword) => {
    const response = await apiClient.post(`${API_URL}/users/change-password/${username}/${newPassword}/${oldPassword}`);
    return response.data;
};

export const addFile = async (projectId, keyName, isImage, files, description) => {
    const formData = new FormData();
    formData.append('isImage', isImage);

    const getFileExtension = (filename) => {
        return filename.split('.').pop() || '';
    };

    for (const [locale, file] of Object.entries(files)) {
        const fileExtension = getFileExtension(file.name);
        const filename = `${locale}.${fileExtension}`;
        formData.append('files', new File([file], filename));
    }

    const response = await apiClient.post(`${API_URL}/file/${projectId}/${keyName}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        params: {
            description
        }
    });

    return response.data;
};

export const deleteFile = async (projectId, keyName) => {
    const response = await apiClient.delete(`${API_URL}/file/${projectId}/${keyName}`, {});
    return response.data;
};

export const editFile = async (projectId, keyName, isImage, files, description) => {
    const formData = new FormData();
    formData.append('isImage', isImage);

    const getFileExtension = (filename) => {
        return filename.split('.').pop() || '';
    };

    for (const [locale, file] of Object.entries(files)) {
        const fileExtension = getFileExtension(file.name);
        const filename = `${locale}.${fileExtension}`;
        formData.append('files', new File([file], filename));
    }

    const response = await apiClient.put(`${API_URL}/file/${projectId}/${keyName}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        params: {
            description
        }
    });

    return response.data;
};

export const getProjectFiles = async (projectId) => {
    const response = await apiClient.get(`${API_URL}/file/${projectId}`);
    return response.data;
};

export const getFilesNoValue = async (projectId) => {
    const response = await apiClient.get(`${API_URL}/file/no-values/${projectId}`);
    return response.data;
};




